/**
 * Flow Slice
 *
 * Manages onboarding flow navigation state
 */
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { OnboardingRecommendations } from '@contracts';
import { fetchRecommendationsThunk } from '../thunks/recommendationsThunks';

// ============================================
// State Types
// ============================================

export interface FlowState {
  currentStep: number;
  recommendations: OnboardingRecommendations | null;
  isRecommendationsLoading: boolean;
  recommendationsError: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialFlowState: FlowState = {
  currentStep: 1,
  recommendations: null,
  isRecommendationsLoading: false,
  recommendationsError: null,
};

// ============================================
// Slice
// ============================================

const flowSlice = createSlice({
  name: 'onboarding/flow',
  initialState: initialFlowState,
  reducers: {
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },

    goToNextStep: (state) => {
      state.currentStep += 1;
    },

    goToPreviousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
      }
    },

    resetFlowState: () => initialFlowState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRecommendationsThunk.pending, (state) => {
        state.isRecommendationsLoading = true;
        state.recommendationsError = null;
      })
      .addCase(fetchRecommendationsThunk.fulfilled, (state, action) => {
        state.recommendations = action.payload;
        state.isRecommendationsLoading = false;
      })
      .addCase(fetchRecommendationsThunk.rejected, (state, action) => {
        state.isRecommendationsLoading = false;
        state.recommendationsError = action.payload ?? 'Unknown error';
      });
  },
});

// ============================================
// Actions
// ============================================

export const { setCurrentStep, goToNextStep, goToPreviousStep, resetFlowState } = flowSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const flowReducer = flowSlice.reducer;
